/**
 * Ticket ME1: Pflegemaske eines Mentorings.
 *
 * Die Abschnittsfolge der öffentlichen Seite ist fest; hier stehen genau die
 * Texte dazu. Ablauf und häufige Fragen sind Listen, weil sie je Mentoring
 * unterschiedlich lang sind – alles Übrige ist Fließtext im Markdown-Editor,
 * wie bei den Freebie-Landingpages.
 *
 * Keine zweite Personenverwaltung: die Betreuung wird aus den Personen des
 * internen Bereichs gewählt (Team → für Website freigegeben).
 */

import { useEffect, useMemo, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowLeft, ExternalLink, Plus, Trash2, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { sanitizeStorageFilename } from "@/lib/storage-filename";
import { confirmDialog } from "@/lib/confirm-dialog";
import { useAdminSession } from "@/hooks/use-admin-session";
import { MarkdownEditor } from "@/components/MarkdownEditor.lazy";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { AdminCard } from "@/components/admin/AdminCard";
import { AdminErrorNotice } from "@/components/admin/AdminErrorNotice";
import { AdminUploadButton } from "@/components/admin/AdminUploadButton";
import {
  MENTORING_DELIVERY_MODES,
  MENTORING_DELIVERY_MODE_LABELS,
  MENTORING_PREFIX,
  parseMentoringDeliveryMode,
  audienceDeleteWarning,
  audienceSlugFromLabel,
  mentoringFormSchema,
  mentoringPath,
  mentoringPublishIssues,
  type MentoringFaqItem,
  type MentoringFormValues,
  type MentoringScheduleItem,
} from "@/lib/mentorings";


export const Route = createFileRoute("/_admin/admin/mentorings/$id")({
  head: () => ({
    meta: [
      { title: "Mentoring – Admin – creategy" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: MentoringDetailPage,
});

/** Vorschaubilder liegen im vorhandenen Bildbereich der Angebote. */
const IMAGE_BUCKET = "service-images";
const IMAGE_PREFIX = "mentorings/";

const EMPTY: MentoringFormValues = {
  slug: "",
  sortOrder: 100,
  status: "entwurf",
  title: "",
  shortDescription: "",
  audienceLabel: "",
  audienceIds: [],
  scopeLabel: "",
  deliveryMode: "online",
  priceLabel: "",
  priceStrikethroughLabel: "",
  mentorProfileId: null,
  descriptionMd: "",
  problemMd: "",
  outcomeMd: "",
  contentsIntroMd: "",
  schedule: [],
  faqs: [],
  previewImagePath: null,
  headerImagePath: null,
  seoTitle: "",
  seoDescription: "",
};

const inputClass = "mt-2 w-full rounded-sm border border-border bg-background px-3 py-2 text-sm";
const labelClass = "block text-sm font-medium";

type MentorOption = { id: string; name: string; jobTitle: string | null; isPublic: boolean };
type AudienceOption = { id: string; label: string; slug: string };

function MentoringDetailPage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const { role } = useAdminSession();
  const isNew = id === "neu";

  const [values, setValues] = useState<MentoringFormValues>(EMPTY);
  const [saving, setSaving] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);
  const [headerUploading, setHeaderUploading] = useState(false);
  const [scheduleUploading, setScheduleUploading] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [newAudience, setNewAudience] = useState("");
  const [showNewAudience, setShowNewAudience] = useState(false);
  const [audienceSaving, setAudienceSaving] = useState(false);


  const record = useQuery({
    queryKey: ["admin-mentoring", id],
    enabled: !isNew,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("mentorings")
        .select("*")
        .eq("id", id)
        .maybeSingle();
      if (error) throw new Error(error.message);
      return data as Record<string, any> | null;
    },
  });

  /** Personen aus dem internen Bereich – keine eigene Verwaltung daneben. */
  const mentors = useQuery({
    queryKey: ["admin-mentoring-people"],
    queryFn: async (): Promise<MentorOption[]> => {
      const { data, error } = await supabase
        .from("profiles")
        .select("id, full_name, job_title, is_public")
        .order("full_name", { ascending: true });
      if (error) throw new Error(error.message);
      return (data ?? []).map((row: any) => ({
        id: row.id as string,
        name: (row.full_name as string | null) ?? "Ohne Namen",
        jobTitle: (row.job_title as string | null) ?? null,
        isPublic: Boolean(row.is_public),
      }));
    },
  });

  /**
   * Ticket ME2: Die pflegbare Liste der Zielgruppen. Neue Zielgruppen entstehen
   * hier im Editor und sind sofort zuweisbar – niemand muss dafür in die
   * Datenbank.
   */
  const audiences = useQuery({
    queryKey: ["admin-mentoring-audiences"],
    queryFn: async (): Promise<AudienceOption[]> => {
      const { data, error } = await supabase
        .from("mentoring_audiences")
        .select("id, label, slug, sort_order")
        .order("sort_order", { ascending: true })
        .order("label", { ascending: true });
      if (error) throw new Error(error.message);
      return (data ?? []).map((row: any) => ({
        id: row.id as string,
        label: (row.label as string) ?? "",
        slug: (row.slug as string) ?? "",
      }));
    },
  });

  const assigned = useQuery({
    queryKey: ["admin-mentoring-audience-assignments", id],
    enabled: !isNew,
    queryFn: async (): Promise<string[]> => {
      const { data, error } = await supabase
        .from("mentoring_audience_assignments")
        .select("audience_id")
        .eq("mentoring_id", id);
      if (error) throw new Error(error.message);
      return (data ?? []).map((row: any) => row.audience_id as string);
    },
  });

  useEffect(() => {
    const row = record.data;
    if (!row) return;
    setValues((prev) => ({
      ...prev,
      slug: row.slug ?? "",
      sortOrder: Number(row.sort_order ?? 100),
      status: row.status ?? "entwurf",
      title: row.title ?? "",
      
      shortDescription: row.short_description ?? "",
      audienceLabel: row.audience_label ?? "",
      scopeLabel: row.scope_label ?? "",
      deliveryMode: parseMentoringDeliveryMode(row.delivery_mode),
      priceLabel: row.price_label ?? "",
      priceStrikethroughLabel: row.price_strikethrough_label ?? "",
      mentorProfileId: row.mentor_profile_id ?? null,
      descriptionMd: row.description_md ?? "",
      problemMd: row.problem_md ?? "",
      outcomeMd: row.outcome_md ?? "",
      contentsIntroMd: row.contents_intro_md ?? "",
      schedule: Array.isArray(row.schedule) ? (row.schedule as MentoringScheduleItem[]) : [],
      faqs: Array.isArray(row.faqs) ? (row.faqs as MentoringFaqItem[]) : [],
      previewImagePath: row.preview_image_path ?? null,
      headerImagePath: row.header_image_path ?? null,
      seoTitle: row.seo_title ?? "",
      seoDescription: row.seo_description ?? "",
    }));
  }, [record.data]);

  useEffect(() => {
    if (!assigned.data) return;
    setValues((prev) => ({ ...prev, audienceIds: assigned.data }));
  }, [assigned.data]);

  function patch(next: Partial<MentoringFormValues>) {
    setValues((prev) => ({ ...prev, ...next }));
  }

  function toggleAudience(audienceId: string) {
    const current = values.audienceIds ?? [];
    patch({
      audienceIds: current.includes(audienceId)
        ? current.filter((x) => x !== audienceId)
        : [...current, audienceId],
    });
  }

  /** Eine neue Zielgruppe anlegen und gleich zuordnen. */
  async function onCreateAudience() {
    const label = newAudience.trim();
    if (!label) return;
    const slug = audienceSlugFromLabel(label);
    if (!slug) {
      toast.error("Bitte eine Bezeichnung mit Buchstaben oder Zahlen angeben.");
      return;
    }
    setAudienceSaving(true);
    try {
      const { data, error } = await supabase
        .from("mentoring_audiences")
        .insert({ label, slug })
        .select("id, label, slug")
        .single();
      if (error) throw new Error(error.message);
      await audiences.refetch();
      patch({ audienceIds: [...(values.audienceIds ?? []), (data as { id: string }).id] });
      setNewAudience("");
      setShowNewAudience(false);
      toast.success("Zielgruppe angelegt und zugeordnet. Bitte noch speichern.");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Zielgruppe konnte nicht angelegt werden.");
    } finally {
      setAudienceSaving(false);
    }
  }

  /**
   * Ticket ME4: Eine Zielgruppe löschen. Vorher steht im Hinweis, an wie vielen
   * Mentorings sie hängt – eine Zielgruppe an sechs Seiten löscht man nicht
   * versehentlich. Die Zuordnungen fallen mit (Fremdschlüssel mit Kaskade), die
   * Mentorings selbst bleiben unberührt.
   */
  async function onDeleteAudience(option: AudienceOption) {
    const { count, error: countError } = await supabase
      .from("mentoring_audience_assignments")
      .select("mentoring_id", { count: "exact", head: true })
      .eq("audience_id", option.id);
    if (countError) {
      toast.error(countError.message);
      return;
    }
    const ok = await confirmDialog({
      title: `Zielgruppe „${option.label}" löschen?`,
      description: audienceDeleteWarning(count ?? 0),
      confirmLabel: "Löschen",
      destructive: true,
    });
    if (!ok) return;
    const { error } = await supabase.from("mentoring_audiences").delete().eq("id", option.id);
    if (error) {
      toast.error(error.message);
      return;
    }
    patch({ audienceIds: (values.audienceIds ?? []).filter((x) => x !== option.id) });
    await audiences.refetch();
    toast.success("Zielgruppe gelöscht.");
  }

  const selectedMentor = useMemo(
    () => (mentors.data ?? []).find((m) => m.id === values.mentorProfileId) ?? null,
    [mentors.data, values.mentorProfileId],
  );

  /**
   * Dieselben zwei Bedingungen, die auch die Datenbank durchsetzt (Betreuung,
   * Preis) – hier nur früher sichtbar. Bilder sind freiwillig: Vorschau- und
   * Headerbild haben unterschiedliche Formate und halten das Veröffentlichen
   * nicht auf.
   */
  const publishIssues = mentoringPublishIssues({
    mentorProfileId: values.mentorProfileId,
    mentorIsPublic: selectedMentor ? selectedMentor.isPublic : undefined,
    priceLabel: values.priceLabel,
  });


  async function onImageUpload(file: File) {
    setImageUploading(true);
    try {
      const path = `${IMAGE_PREFIX}${crypto.randomUUID()}/${sanitizeStorageFilename(file.name)}`;
      const { error } = await supabase.storage
        .from(IMAGE_BUCKET)
        .upload(path, file, { upsert: false, contentType: file.type });
      if (error) throw new Error(error.message);
      const previous = values.previewImagePath;
      patch({ previewImagePath: path });
      if (previous) await supabase.storage.from(IMAGE_BUCKET).remove([previous]);
      toast.success("Vorschaubild hinterlegt. Bitte noch speichern.");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Upload fehlgeschlagen.");
    } finally {
      setImageUploading(false);
    }
  }

  async function onImageRemove() {
    const previous = values.previewImagePath;
    if (!previous) return;
    patch({ previewImagePath: null });
    await supabase.storage.from(IMAGE_BUCKET).remove([previous]);
    toast.success("Vorschaubild entfernt. Bitte noch speichern.");
  }

  /**
   * Headerbild: das längliche Foto oben auf der Detailseite. Getrennt vom
   * quadratischen Vorschaubild, weil ein Bild nicht beide Formate bedient.
   */
  async function onHeaderImageUpload(file: File) {
    setHeaderUploading(true);
    try {
      const path = `${IMAGE_PREFIX}${crypto.randomUUID()}/${sanitizeStorageFilename(file.name)}`;
      const { error } = await supabase.storage
        .from(IMAGE_BUCKET)
        .upload(path, file, { upsert: false, contentType: file.type });
      if (error) throw new Error(error.message);
      const previous = values.headerImagePath;
      patch({ headerImagePath: path });
      if (previous) await supabase.storage.from(IMAGE_BUCKET).remove([previous]);
      toast.success("Headerbild hinterlegt. Bitte noch speichern.");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Upload fehlgeschlagen.");
    } finally {
      setHeaderUploading(false);
    }
  }

  async function onHeaderImageRemove() {
    const previous = values.headerImagePath;
    if (!previous) return;
    patch({ headerImagePath: null });
    await supabase.storage.from(IMAGE_BUCKET).remove([previous]);
    toast.success("Headerbild entfernt. Bitte noch speichern.");
  }

  /**
   * Ticket ME3: Je Ablaufeintrag ein Bild. Liegt keines vor, entfällt die
   * Bildstelle auf der Seite ersatzlos – kein Platzhalterbild.
   */
  async function onScheduleImageUpload(index: number, file: File) {
    setScheduleUploading(index);
    try {
      const path = `${IMAGE_PREFIX}${crypto.randomUUID()}/${sanitizeStorageFilename(file.name)}`;
      const { error } = await supabase.storage
        .from(IMAGE_BUCKET)
        .upload(path, file, { upsert: false, contentType: file.type });
      if (error) throw new Error(error.message);
      const previous = (values.schedule ?? [])[index]?.imagePath ?? null;
      updateSchedule(index, { imagePath: path });
      if (previous) await supabase.storage.from(IMAGE_BUCKET).remove([previous]);
      toast.success("Bild hinterlegt. Bitte noch speichern.");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Upload fehlgeschlagen.");
    } finally {
      setScheduleUploading(null);
    }
  }

  async function onScheduleImageRemove(index: number) {
    const previous = (values.schedule ?? [])[index]?.imagePath ?? null;
    if (!previous) return;
    updateSchedule(index, { imagePath: "" });
    await supabase.storage.from(IMAGE_BUCKET).remove([previous]);
    toast.success("Bild entfernt. Bitte noch speichern.");
  }

  /**
   * Ticket ME2: Die Zuordnung der Zielgruppen liegt in einer eigenen Tabelle.
   * Gespeichert wird als Abgleich: fortgefallene Zuordnungen weg, neue dazu.
   */
  async function saveAudienceAssignments(mentoringId: string, wanted: string[]) {
    const { data, error } = await supabase
      .from("mentoring_audience_assignments")
      .select("audience_id")
      .eq("mentoring_id", mentoringId);
    if (error) throw new Error(error.message);
    const before = (data ?? []).map((row: any) => row.audience_id as string);
    const removed = before.filter((x) => !wanted.includes(x));
    const added = wanted.filter((x) => !before.includes(x));
    if (removed.length > 0) {
      const { error: delError } = await supabase
        .from("mentoring_audience_assignments")
        .delete()
        .eq("mentoring_id", mentoringId)
        .in("audience_id", removed);
      if (delError) throw new Error(delError.message);
    }
    if (added.length > 0) {
      const { error: insError } = await supabase
        .from("mentoring_audience_assignments")
        .insert(added.map((audience_id) => ({ mentoring_id: mentoringId, audience_id })));
      if (insError) throw new Error(insError.message);
    }
  }

  async function onSave() {
    const parsed = mentoringFormSchema.safeParse(values);
    if (!parsed.success) {
      toast.error(parsed.error.issues[0]?.message ?? "Bitte Eingaben prüfen.");
      return;
    }
    const v = parsed.data;
    setSaving(true);
    try {
      const payload = {
        slug: v.slug,
        sort_order: v.sortOrder,
        status: v.status,
        title: v.title,
        
        short_description: v.shortDescription,
        audience_label: v.audienceLabel,
        scope_label: v.scopeLabel,
        delivery_mode: v.deliveryMode,
        price_label: v.priceLabel,
        price_strikethrough_label: (v.priceStrikethroughLabel ?? "").trim() || null,
        mentor_profile_id: v.mentorProfileId,
        description_md: v.descriptionMd,
        problem_md: v.problemMd,
        outcome_md: v.outcomeMd,
        contents_intro_md: v.contentsIntroMd,
        schedule: v.schedule,
        faqs: v.faqs,
        preview_image_path: v.previewImagePath,
        header_image_path: v.headerImagePath ?? null,
        seo_title: v.seoTitle,
        seo_description: v.seoDescription,
      };
      if (isNew) {
        const { data, error } = await supabase
          .from("mentorings")
          .insert(payload)
          .select("id")
          .single();
        if (error) throw new Error(error.message);
        const newId = (data as { id: string }).id;
        await saveAudienceAssignments(newId, v.audienceIds ?? []);
        toast.success("Mentoring angelegt.");
        navigate({ to: "/admin/mentorings/$id", params: { id: newId } });
      } else {
        const { error } = await supabase.from("mentorings").update(payload).eq("id", id);
        if (error) throw new Error(error.message);
        await saveAudienceAssignments(id, v.audienceIds ?? []);
        toast.success("Gespeichert.");
        await Promise.all([record.refetch(), assigned.refetch()]);
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  }


  async function onDelete() {
    const ok = await confirmDialog({
      title: `„${values.title || values.slug}“ löschen?`,
      description:
        "Die Seite verschwindet damit vollständig. Soll sie nur vorübergehend verschwinden, setzen Sie den Status auf Entwurf.",
      confirmLabel: "Löschen",
      destructive: true,
    });
    if (!ok) return;
    setDeleting(true);
    try {
      const { error } = await supabase.from("mentorings").delete().eq("id", id);
      if (error) throw new Error(error.message);
      toast.success("Gelöscht.");
      navigate({ to: "/admin/mentorings" });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Löschen fehlgeschlagen.");
    } finally {
      setDeleting(false);
    }
  }

  function updateSchedule(index: number, next: Partial<MentoringScheduleItem>) {
    patch({
      schedule: (values.schedule ?? []).map((item, i) =>
        i === index ? { ...item, ...next } : item,
      ),
    });
  }

  function updateFaq(index: number, next: Partial<MentoringFaqItem>) {
    patch({ faqs: (values.faqs ?? []).map((item, i) => (i === index ? { ...item, ...next } : item)) });
  }

  if (record.isError) {
    return <AdminErrorNotice message={(record.error as Error)?.message} />;
  }

  return (
    <div className="space-y-8 pb-24">
      <AdminPageHeader
        title={isNew ? "Mentoring anlegen" : values.title || "Mentoring"}
        description={
          <>
            Die Abschnittsfolge der öffentlichen Seite ist für alle Mentorings gleich.{" "}
            {!isNew && values.slug ? (
              <a
                href={mentoringPath(values.slug)}
                target="_blank"
                rel="noopener"
                className="inline-flex items-center gap-1 text-primary hover:underline"
              >
                Seite ansehen <ExternalLink className="h-3.5 w-3.5" aria-hidden />
              </a>
            ) : null}
          </>
        }
        actions={
          <>
            <button
              type="button"
              onClick={() => navigate({ to: "/admin/mentorings" })}
              className="inline-flex items-center gap-2 rounded-sm border border-border px-3 py-2 text-sm"
            >
              <ArrowLeft className="h-4 w-4" aria-hidden /> Zurück
            </button>
            <button
              type="button"
              disabled={saving}
              onClick={() => void onSave()}
              className="rounded-sm bg-brand-button px-4 py-2 font-display text-sm font-extrabold text-brand-button-foreground disabled:opacity-60"
            >
              {saving ? "Speichern …" : "Speichern"}
            </button>
          </>
        }
      />

      <AdminCard title="Grunddaten">
        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <div>
            <label className={labelClass} htmlFor="me-title">
              Titel
            </label>
            <input
              id="me-title"
              className={inputClass}
              value={values.title}
              onChange={(e) => patch({ title: e.target.value })}
            />
          </div>
          <div>
            <label className={labelClass} htmlFor="me-slug">
              Kürzel in der Adresse
            </label>
            <input
              id="me-slug"
              className={inputClass}
              value={values.slug}
              onChange={(e) => patch({ slug: e.target.value })}
            />
            <p className="mt-2 font-body text-xs text-muted-foreground">
              Ergibt {MENTORING_PREFIX}/{values.slug || "kuerzel"}.
            </p>
          </div>
          <div>
            <label className={labelClass} htmlFor="me-sort">
              Reihenfolge
            </label>
            <input
              id="me-sort"
              type="number"
              min={0}
              max={9999}
              className={inputClass}
              value={values.sortOrder}
              onChange={(e) => patch({ sortOrder: Number(e.target.value) })}
            />
            <p className="mt-2 font-body text-xs text-muted-foreground">
              Kleine Zahl steht vorn. Bei gleicher Zahl entscheidet der Titel.
            </p>
          </div>
          <div>
            <label className={labelClass} htmlFor="me-scope">
              Dauer
            </label>
            <input
              id="me-scope"
              className={inputClass}
              value={values.scopeLabel}
              onChange={(e) => patch({ scopeLabel: e.target.value })}
              placeholder="z. B. 4 Termine à 90 Minuten"
            />
          </div>
          <div className="md:col-span-2">
            <label className={labelClass} htmlFor="me-short">
              Kurzbeschreibung für die Übersicht
            </label>
            <textarea
              id="me-short"
              rows={3}
              maxLength={600}
              className={inputClass}
              value={values.shortDescription}
              onChange={(e) => patch({ shortDescription: e.target.value })}
            />
          </div>
          <div>
            <label className={labelClass} htmlFor="me-price">
              Preis (Bruttopreis, Klartext)
            </label>
            <input
              id="me-price"
              className={inputClass}
              value={values.priceLabel}
              onChange={(e) => patch({ priceLabel: e.target.value })}
              placeholder="z. B. 2.400 €"
            />
            <p className="mt-2 font-body text-xs text-muted-foreground">
              Der tatsächlich geltende Preis. Alle Mentoring-Preise sind Bruttopreise; der
              Hinweis „inkl. MwSt." wird auf den Seiten automatisch ergänzt. Ohne Preis kann nicht
              veröffentlicht werden.
            </p>
          </div>
          <div>
            <label className={labelClass} htmlFor="me-price-strike">
              Streichpreis (freiwillig)
            </label>
            <input
              id="me-price-strike"
              className={inputClass}
              value={values.priceStrikethroughLabel ?? ""}
              onChange={(e) => patch({ priceStrikethroughLabel: e.target.value })}
              placeholder="z. B. 1.990 €"
            />
            <p className="mt-2 font-body text-xs text-muted-foreground">
              Erscheint durchgestrichen neben dem Preis, ebenfalls als Bruttopreis. Bleibt das Feld
              leer, steht dort nur der Preis.
            </p>
          </div>

          {/*
            Ticket ME4: Zielgruppen und Betreuung stehen in den Grunddaten, gleich
            oben bei den übrigen Kopfangaben – ein Klick statt zweier Karten
            weiter unten.
          */}
          <div>
            <span className={labelClass}>Wie das Mentoring stattfindet</span>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              {MENTORING_DELIVERY_MODES.map((mode) => {
                const active = (values.deliveryMode ?? "online") === mode;
                return (
                  <button
                    key={mode}
                    type="button"
                    aria-pressed={active}
                    onClick={() => patch({ deliveryMode: mode })}
                    className={
                      active
                        ? "rounded-sm border border-primary bg-primary px-3 py-2 font-body text-sm text-primary-foreground"
                        : "rounded-sm border border-border px-3 py-2 font-body text-sm text-foreground hover:border-primary"
                    }
                  >
                    {MENTORING_DELIVERY_MODE_LABELS[mode]}
                  </button>
                );
              })}
            </div>
            <p className="mt-2 font-body text-xs text-muted-foreground">
              Ein Wert je Mentoring. Die Angabe erscheint im Kopfbereich der Seite neben Dauer und
              Preis.
            </p>
          </div>

          <div>
            <label className={labelClass} htmlFor="me-mentor">
              Betreuung
            </label>
            <select
              id="me-mentor"
              className={inputClass}
              value={values.mentorProfileId ?? ""}
              onChange={(e) => patch({ mentorProfileId: e.target.value || null })}
            >
              <option value="">Keine Zuordnung</option>
              {(mentors.data ?? []).map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                  {m.jobTitle ? ` – ${m.jobTitle}` : ""}
                  {m.isPublic ? "" : " (nicht für die Website freigegeben)"}
                </option>
              ))}
            </select>
            <p className="mt-2 font-body text-xs text-muted-foreground">
              Die Personen kommen aus dem internen Bereich (Team). Bild, Name, Rolle und
              Vorstellungstext werden dort gepflegt – hier wird nur zugeordnet.
            </p>
            {selectedMentor && !selectedMentor.isPublic && (
              <p className="mt-2 font-body text-sm text-destructive">
                Diese Person ist im Team nicht für die Website freigegeben. Solange das so bleibt,
                lässt sich die Seite nicht veröffentlichen.
              </p>
            )}
          </div>

          <div className="md:col-span-2">
            <span className={labelClass}>Zielgruppen</span>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              {(audiences.data ?? []).length === 0 && (
                <p className="font-body text-sm text-muted-foreground">
                  Noch keine Zielgruppe angelegt.
                </p>
              )}
              {(audiences.data ?? []).map((option) => {
                const active = (values.audienceIds ?? []).includes(option.id);
                return (
                  <span
                    key={option.id}
                    className={
                      active
                        ? "inline-flex items-center gap-1 rounded-full border border-primary bg-primary pl-3 pr-1 py-1 font-display text-xs font-extrabold text-primary-foreground"
                        : "inline-flex items-center gap-1 rounded-full border border-border pl-3 pr-1 py-1 font-display text-xs font-extrabold text-foreground hover:border-primary"
                    }
                  >
                    <button
                      type="button"
                      aria-pressed={active}
                      onClick={() => toggleAudience(option.id)}
                    >
                      {option.label}
                    </button>
                    {role === "admin" && (
                      <button
                        type="button"
                        aria-label={`Zielgruppe „${option.label}" löschen`}
                        onClick={() => void onDeleteAudience(option)}
                        className="rounded-full p-1 opacity-70 hover:opacity-100"
                      >
                        <X className="h-3 w-3" aria-hidden />
                      </button>
                    )}
                  </span>
                );
              })}
              <button
                type="button"
                aria-label="Neue Zielgruppe anlegen"
                onClick={() => setShowNewAudience((v) => !v)}
                className="inline-flex items-center gap-1 rounded-full border border-dashed border-border px-3 py-1 font-display text-xs font-extrabold text-foreground hover:border-primary"
              >
                <Plus className="h-3.5 w-3.5" aria-hidden /> Neu
              </button>
            </div>
            {showNewAudience && (
              <div className="mt-3 flex flex-wrap items-center gap-3">
                <input
                  aria-label="Bezeichnung der neuen Zielgruppe"
                  className="w-full max-w-[320px] rounded-sm border border-border bg-background px-3 py-2 text-sm"
                  value={newAudience}
                  onChange={(e) => setNewAudience(e.target.value)}
                  placeholder="z. B. Gemeinderäte"
                />
                <button
                  type="button"
                  disabled={audienceSaving || !newAudience.trim()}
                  onClick={() => void onCreateAudience()}
                  className="rounded-sm border border-border px-3 py-2 text-sm disabled:opacity-60"
                >
                  {audienceSaving ? "Anlegen …" : "Anlegen und zuordnen"}
                </button>
              </div>
            )}
            <p className="mt-2 font-body text-xs text-muted-foreground">
              Mehrere Zielgruppen sind möglich; auf den Seiten erscheinen sie als kleine Pillen. Eine
              neue Zielgruppe ist sofort zuweisbar. Wird eine gelöscht, verschwindet sie bei allen
              Mentorings – die Mentorings selbst bleiben unverändert.
            </p>
          </div>


          <div>
            <label className={labelClass} htmlFor="me-status">
              Status
            </label>
            <select
              id="me-status"
              className={inputClass}
              value={values.status}
              onChange={(e) => patch({ status: e.target.value as MentoringFormValues["status"] })}
            >
              <option value="entwurf">Entwurf</option>
              <option value="veroeffentlicht">Veröffentlicht</option>
            </select>
          </div>
          <div>
            <span className={labelClass}>Vorschaubild</span>
            <div className="mt-2">
              <AdminUploadButton
                label={values.previewImagePath ? "Vorschaubild ersetzen" : "Vorschaubild hochladen"}
                accept="image/*"
                uploading={imageUploading}
                currentName={values.previewImagePath ? values.previewImagePath.split("/").pop() : null}
                hint="Quadratisch, empfohlen 800 × 800 Pixel. Erscheint auf der Übersicht und beim Teilen. JPG, PNG oder WebP. Freiwillig."
                onFiles={(files) => {
                  const file = files[0];
                  if (file) void onImageUpload(file);
                }}
              >
                {values.previewImagePath ? (
                  <button
                    type="button"
                    onClick={() => void onImageRemove()}
                    className="inline-flex items-center gap-2 rounded-md border border-destructive/40 px-3 py-2 text-xs font-semibold text-destructive hover:bg-destructive/10"
                  >
                    <Trash2 className="h-3.5 w-3.5" aria-hidden /> Entfernen
                  </button>
                ) : null}
              </AdminUploadButton>
            </div>
          </div>
          <div>
            <span className={labelClass}>Headerbild</span>
            <div className="mt-2">
              <AdminUploadButton
                label={values.headerImagePath ? "Headerbild ersetzen" : "Headerbild hochladen"}
                accept="image/*"
                uploading={headerUploading}
                currentName={values.headerImagePath ? values.headerImagePath.split("/").pop() : null}
                hint="Querformat, empfohlen 1600 × 640 Pixel. Erscheint groß oben auf der Detailseite. Fehlt es, wird dort das Vorschaubild gezeigt. JPG, PNG oder WebP. Freiwillig."
                onFiles={(files) => {
                  const file = files[0];
                  if (file) void onHeaderImageUpload(file);
                }}
              >
                {values.headerImagePath ? (
                  <button
                    type="button"
                    onClick={() => void onHeaderImageRemove()}
                    className="inline-flex items-center gap-2 rounded-md border border-destructive/40 px-3 py-2 text-xs font-semibold text-destructive hover:bg-destructive/10"
                  >
                    <Trash2 className="h-3.5 w-3.5" aria-hidden /> Entfernen
                  </button>
                ) : null}
              </AdminUploadButton>
            </div>
          </div>
        </div>
        {publishIssues.length > 0 && (
          <ul className="mt-4 space-y-1 rounded-sm border border-destructive/40 bg-destructive/5 p-4 font-body text-sm text-destructive">
            {publishIssues.map((issue) => (
              <li key={issue}>{issue}</li>
            ))}
          </ul>
        )}
      </AdminCard>

      {/*
        Ticket ME7: Beschreibung über den beiden Kästen. Steht hier unter den
        übrigen Textabschnitten; auf der Seite erscheint sie als gewöhnlicher
        Fließtext zwischen Kopfbereich und Kästen. Bleibt sie leer, entfällt der
        Bereich dort ersatzlos.
      */}
      <AdminCard
        title="Beschreibung (über den beiden Kästen)"
        description="Erscheint auf der Seite zwischen Kopfbereich und den Kästen „Das Problem“ und „Was Sie danach können“, als Fließtext über die volle Breite. Mehrere Absätze sind erwünscht – der Text zählt für die Auffindbarkeit in Suchmaschinen. Bleibt das Feld leer, entfällt der Bereich ersatzlos."
      >
        <div className="mt-4">
          <MarkdownEditor
            value={values.descriptionMd ?? ""}
            onChange={(v) => patch({ descriptionMd: v })}
            rows={14}
          />
        </div>
      </AdminCard>


      {(
        [
          ["Das Problem", "problemMd"],
          ["Was Sie danach können", "outcomeMd"],
        ] as Array<[string, keyof MentoringFormValues]>
      ).map(([title, key]) => (
        <AdminCard key={key} title={title}>
          <div className="mt-4">
            <MarkdownEditor
              value={(values[key] as string) ?? ""}
              onChange={(v) => patch({ [key]: v } as Partial<MentoringFormValues>)}
              rows={10}
            />
          </div>
        </AdminCard>
      ))}

      <AdminCard
        title="Die Inhalte des Mentorings"
        description="Ein kurzer Vorspann und eine Zeile je Termin. Die Länge der Liste ist je Mentoring verschieden."
        actions={
          <button
            type="button"
            onClick={() =>
              patch({
                schedule: [
                  ...(values.schedule ?? []),
                  { label: "", content: "", duration: "", imagePath: "", highlight: false },
                ],
              })
            }
            className="rounded-sm border border-border px-3 py-2 text-sm"
          >
            Termin hinzufügen
          </button>
        }
      >
        {/*
          Ticket ME5: Vorspann unter der Überschrift „Die Inhalte des
          Mentorings". Bleibt er leer, entfällt er auf der Seite ersatzlos.
          Der Text des früheren Abschnitts „Was enthalten ist" steht hier.
        */}
        <div className="mt-4">
          <span className={labelClass}>Vorspann unter der Überschrift (freiwillig)</span>
          <div className="mt-2">
            <MarkdownEditor
              value={values.contentsIntroMd ?? ""}
              onChange={(v) => patch({ contentsIntroMd: v })}
              rows={6}
            />
          </div>
        </div>

        <div className="mt-4 space-y-4">
          {(values.schedule ?? []).length === 0 && (
            <p className="font-body text-sm text-muted-foreground">Noch kein Termin eingetragen.</p>
          )}
          {(values.schedule ?? []).map((item, index) => (
            <div key={index} className="rounded-sm border border-border p-4">
              <div className="grid items-start gap-3 md:grid-cols-[minmax(0,2fr)_minmax(0,1fr)_auto]">
                <input
                  className={inputClass}
                  placeholder="Bezeichnung"
                  value={item.label}
                  onChange={(e) => updateSchedule(index, { label: e.target.value })}
                />
                <input
                  className={inputClass}
                  placeholder="Dauer, z. B. 90 Minuten"
                  value={item.highlight ? "" : item.duration ?? ""}
                  disabled={Boolean(item.highlight)}
                  onChange={(e) => updateSchedule(index, { duration: e.target.value })}
                />
                <button
                  type="button"
                  onClick={() =>
                    patch({ schedule: (values.schedule ?? []).filter((_, i) => i !== index) })
                  }
                  className="inline-flex shrink-0 items-center justify-center gap-2 rounded-sm border border-destructive/40 px-3 py-2 text-xs font-semibold text-destructive hover:bg-destructive/10"
                >
                  <Trash2 className="h-3.5 w-3.5" aria-hidden /> Termin entfernen
                </button>
              </div>
              {/*
                Ticket ME6: Ein abgesetzter Eintrag ist kein Termin (etwa das
                Mentoringmaterial). Er erhält links einen eigenen Rahmen und
                trägt keine Dauer.
              */}
              <label className="mt-3 flex items-center gap-2 font-body text-sm text-foreground">
                <input
                  type="checkbox"
                  checked={Boolean(item.highlight)}
                  onChange={(e) =>
                    updateSchedule(
                      index,
                      e.target.checked ? { highlight: true, duration: "" } : { highlight: false },
                    )
                  }
                />
                Abgesetzt darstellen (kein Termin, ohne Dauer)
              </label>
              <textarea
                rows={3}
                className={inputClass}
                placeholder="Inhalt"
                value={item.content ?? ""}
                onChange={(e) => updateSchedule(index, { content: e.target.value })}
              />
              <div className="mt-3">
                <AdminUploadButton
                  label={item.imagePath ? "Bild ersetzen" : "Bild hochladen"}
                  accept="image/*"
                  uploading={scheduleUploading === index}
                  currentName={item.imagePath ? item.imagePath.split("/").pop() : null}
                  hint="Ohne Bild erscheint an dieser Stelle nichts."
                  onFiles={(files) => {
                    const file = files[0];
                    if (file) void onScheduleImageUpload(index, file);
                  }}
                >
                  {item.imagePath ? (
                    <button
                      type="button"
                      onClick={() => void onScheduleImageRemove(index)}
                      className="inline-flex items-center gap-2 rounded-md border border-destructive/40 px-3 py-2 text-xs font-semibold text-destructive hover:bg-destructive/10"
                    >
                      <Trash2 className="h-3.5 w-3.5" aria-hidden /> Bild entfernen
                    </button>
                  ) : null}
                </AdminUploadButton>
              </div>
            </div>
          ))}
        </div>
      </AdminCard>

      <AdminCard
        title="Häufige Fragen"
        actions={
          <button
            type="button"
            onClick={() => patch({ faqs: [...(values.faqs ?? []), { question: "", answer: "" }] })}
            className="rounded-sm border border-border px-3 py-2 text-sm"
          >
            Frage hinzufügen
          </button>
        }
      >
        <div className="mt-4 space-y-4">
          {(values.faqs ?? []).length === 0 && (
            <p className="font-body text-sm text-muted-foreground">Noch keine Frage eingetragen.</p>
          )}
          {(values.faqs ?? []).map((item, index) => (
            <div key={index} className="rounded-sm border border-border p-4">
              <input
                className={inputClass}
                placeholder="Frage"
                value={item.question}
                onChange={(e) => updateFaq(index, { question: e.target.value })}
              />
              <textarea
                rows={3}
                className={inputClass}
                placeholder="Antwort"
                value={item.answer ?? ""}
                onChange={(e) => updateFaq(index, { answer: e.target.value })}
              />
              <button
                type="button"
                onClick={() => patch({ faqs: (values.faqs ?? []).filter((_, i) => i !== index) })}
                className="mt-3 inline-flex items-center gap-2 rounded-sm border border-destructive/40 px-3 py-2 text-xs font-semibold text-destructive hover:bg-destructive/10"
              >
                <Trash2 className="h-3.5 w-3.5" aria-hidden /> Frage entfernen
              </button>
            </div>
          ))}
        </div>
      </AdminCard>

      <AdminCard
        title="Angaben für Suchmaschinen"
        description="Bleiben die Felder leer, gilt die Ableitung aus Titel und Kurzbeschreibung."
      >
        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <div>
            <label className={labelClass} htmlFor="me-seo-title">
              Titel für Suchmaschinen
            </label>
            <input
              id="me-seo-title"
              className={inputClass}
              maxLength={200}
              value={values.seoTitle}
              onChange={(e) => patch({ seoTitle: e.target.value })}
            />
          </div>
          <div>
            <label className={labelClass} htmlFor="me-seo-description">
              Beschreibung für Suchmaschinen
            </label>
            <textarea
              id="me-seo-description"
              rows={3}
              maxLength={400}
              className={inputClass}
              value={values.seoDescription}
              onChange={(e) => patch({ seoDescription: e.target.value })}
            />
          </div>
        </div>
      </AdminCard>

      {!isNew && role === "admin" && (
        <AdminCard title="Löschen" description="Nur für Administratorinnen.">
          <button
            type="button"
            disabled={deleting}
            onClick={() => void onDelete()}
            className="mt-4 inline-flex items-center gap-2 rounded-sm border border-destructive/40 px-4 py-2 text-sm font-semibold text-destructive hover:bg-destructive/10 disabled:opacity-60"
          >
            <Trash2 className="h-4 w-4" aria-hidden /> Mentoring löschen
          </button>
        </AdminCard>
      )}
    </div>
  );
}
